import React, { useState } from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  Stack,
  Avatar,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

// Editable Augmont record: name, description, image and status can all be updated.
// Pass the existing record in via `data`.
const defaultData = {
  name: "Augmont Gold Coin 1g",
  description: "24K 999 purity gold coin, hallmarked.",
  status: true, // true = active, false = inactive
  imageUrl: "",
};

export default function AugmontEditForm({ data, onSubmit }) {
  const record = { ...defaultData, ...data };

  const [form, setForm] = useState({
    name: record.name,
    description: record.description,
    status: record.status,
    imageUrl: record.imageUrl,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleStatusChange = (event) => {
    setForm((prev) => ({ ...prev, status: event.target.checked }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.imageUrl.trim()) newErrors.imageUrl = "Image URL is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      ...form,
      imageUrl: form.imageUrl.trim(),
      status: Boolean(form.status),
    };

    onSubmit
      ? onSubmit(payload)
      : console.log("Augmont edit payload:", payload);
  };

  const handleReset = () => {
    setForm({
      name: record.name,
      description: record.description,
      status: record.status,
      imageUrl: record.imageUrl,
    });
    setErrors({});
  };

  return (
    <Paper
      elevation={2}
      sx={{ p: { xs: 2, sm: 4 }, maxWidth: 600, mx: "auto", borderRadius: 3 }}
    >
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Edit Augmont
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update the name, description, image and status.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={form.description}
              onChange={handleChange("description")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Image URL *
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                variant="rounded"
                src={form.imageUrl || undefined}
                sx={{ width: 88, height: 88, bgcolor: "grey.100" }}
              >
                {!form.imageUrl && <AddPhotoAlternateIcon color="disabled" />}
              </Avatar>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/image.jpg"
                value={form.imageUrl}
                onChange={handleChange("imageUrl")}
                error={!!errors.imageUrl}
                helperText={errors.imageUrl}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={!!form.status} onChange={handleStatusChange} />
              }
              label={form.status ? "Active" : "Inactive"}
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 4 }}
        >
          <Button variant="text" color="inherit" onClick={handleReset}>
            Reset
          </Button>
          <Button type="submit" variant="contained" size="large">
            Save Changes
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
